(() => {
  const STORAGE_KEY = 'beurredor_delivery_postcode';
  const PACA_PREFIXES = new Set(['04', '05', '06', '13', '83', '84']);
  const CUTOFF_HOUR = 10;
  const CARD_SELECTOR = 'product-card, .resource-card[data-resource-type="product"]';
  const CARD_MARKER = 'data-beurredor-delivery-ready';
  const MODAL_ID = 'beurredor-postcode-modal';
  let observerScheduled = false;

  const config = window.BeurredorDeliveryConfig || {};

  function normalizePostcode(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 5);
    return /^\d{5}$/.test(digits) ? digits : '';
  }

  function getStoredPostcode() {
    try {
      return normalizePostcode(localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return '';
    }
  }

  function getPostcode() {
    return getStoredPostcode() || normalizePostcode(config.customerPostcode);
  }

  function savePostcode(postcode) {
    try {
      localStorage.setItem(STORAGE_KEY, postcode);
    } catch (error) {
      // Storage can be unavailable in private browsing; the current page will still update.
    }
    document.documentElement.dataset.beurredorPostcode = postcode;
  }

  function parisNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' }));
  }

  function cloneDate(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month, day, 12, 0, 0, 0);
  }

  function dateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function addCalendarDays(date, days) {
    const result = cloneDate(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  function frenchHolidays(year) {
    const easter = easterSunday(year);
    return new Set([
      `${year}-01-01`,
      `${year}-05-01`,
      `${year}-05-08`,
      `${year}-07-14`,
      `${year}-08-15`,
      `${year}-11-01`,
      `${year}-11-11`,
      `${year}-12-25`,
      dateKey(addCalendarDays(easter, 1)),
      dateKey(addCalendarDays(easter, 39)),
      dateKey(addCalendarDays(easter, 50)),
    ]);
  }

  function isBusinessDay(date) {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;
    return !frenchHolidays(date.getFullYear()).has(dateKey(date));
  }

  function nextBusinessDay(date) {
    let result = cloneDate(date);
    do {
      result.setDate(result.getDate() + 1);
    } while (!isBusinessDay(result));
    return result;
  }

  function addBusinessDays(date, count) {
    let result = cloneDate(date);
    let remaining = count;
    while (remaining > 0) {
      result = nextBusinessDay(result);
      remaining -= 1;
    }
    return result;
  }

  function processingDate(now) {
    const today = cloneDate(now);
    if (isBusinessDay(today) && now.getHours() < CUTOFF_HOUR) return today;
    return nextBusinessDay(today);
  }

  function formatDate(date) {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'Europe/Paris',
    }).format(date);
  }

  function estimateFor(postcode) {
    const now = parisNow();
    const prepared = processingDate(now);
    const isPaca = PACA_PREFIXES.has(postcode.slice(0, 2));
    const earliest = addBusinessDays(prepared, 1);
    const latest = isPaca ? earliest : addBusinessDays(prepared, 2);
    return { isPaca, earliest, latest };
  }

  function deliveryText(postcode) {
    if (!postcode) {
      return {
        date: 'PACA 24 h · France 24–48 h ouvrées',
        location: 'Voir ma date de livraison',
        precise: false,
      };
    }

    const estimate = estimateFor(postcode);
    if (estimate.isPaca) {
      return {
        date: `Chez vous dès ${formatDate(estimate.earliest)}`,
        location: `Livraison à ${postcode} · Modifier`,
        precise: true,
      };
    }

    return {
      date: `Chez vous entre ${formatDate(estimate.earliest)} et ${formatDate(estimate.latest)}`,
      location: `Livraison à ${postcode} · Modifier`,
      precise: true,
    };
  }

  function createCardEstimate() {
    const wrapper = document.createElement('div');
    wrapper.className = 'beurredor-card-delivery';
    wrapper.setAttribute('data-beurredor-delivery-card', '');
    wrapper.innerHTML = `
      <div class="beurredor-card-delivery__title"><span aria-hidden="true">🚚</span> Livraison réfrigérée par Chronofresh</div>
      <div class="beurredor-card-delivery__date" data-beurredor-date></div>
      <button type="button" class="beurredor-delivery-location" data-beurredor-postcode-edit></button>
    `;
    return wrapper;
  }

  function installCardEstimate(card) {
    if (!(card instanceof HTMLElement) || card.hasAttribute(CARD_MARKER)) return;
    const content = card.matches('product-card')
      ? card.querySelector('.product-card__content')
      : card.querySelector('.resource-card__content');
    if (!content) return;

    card.setAttribute(CARD_MARKER, 'true');
    content.appendChild(createCardEstimate());
  }

  function updateCardEstimates(postcode) {
    const text = deliveryText(postcode);
    const signature = `${postcode || 'generic'}|${text.date}`;
    document.querySelectorAll('[data-beurredor-delivery-card]').forEach((wrapper) => {
      if (wrapper.dataset.beurredorSignature === signature) return;
      const date = wrapper.querySelector('[data-beurredor-date]');
      const location = wrapper.querySelector('[data-beurredor-postcode-edit]');
      if (date && date.textContent !== text.date) date.textContent = text.date;
      if (location && location.textContent !== text.location) location.textContent = text.location;
      wrapper.toggleAttribute('data-precise', text.precise);
      wrapper.dataset.beurredorSignature = signature;
    });
  }

  function productDeliveryMarkup(postcode) {
    const text = deliveryText(postcode);
    return `
      <div class="beurredor-express-delivery__heading">
        <span aria-hidden="true">🚚</span>
        <strong>Livraison réfrigérée par Chronofresh</strong>
      </div>
      <div class="beurredor-express-delivery__date" data-beurredor-product-date>${text.date}</div>
      <button type="button" class="beurredor-delivery-location beurredor-delivery-location--product" data-beurredor-postcode-edit>${text.location}</button>
      <div class="beurredor-express-delivery__details">
        <span><strong>PACA :</strong> livraison 24 h</span>
        <span><strong>France :</strong> livraison 24–48 h ouvrées</span>
        <span>Frais de livraison calculés automatiquement selon l’adresse avant le paiement.</span>
      </div>
    `;
  }

  function updateProductEstimate(postcode) {
    const box = document.getElementById('beurredor-delivery-box');
    if (!box) return;
    box.hidden = false;
    const text = deliveryText(postcode);
    const signature = `${postcode || 'generic'}|${text.date}`;
    if (box.dataset.beurredorSignature === signature) return;
    box.classList.add('beurredor-delivery-box--express');
    box.innerHTML = productDeliveryMarkup(postcode);
    box.dataset.beurredorSignature = signature;
  }

  function installAll() {
    const postcode = getPostcode();
    if (!postcode) {
      document.querySelectorAll('[data-beurredor-delivery-card]').forEach((element) => element.remove());
      updateProductEstimate('');
      return;
    }
    savePostcode(postcode);
    document.querySelectorAll(CARD_SELECTOR).forEach(installCardEstimate);
    updateCardEstimates(postcode);
    updateProductEstimate(postcode);
  }

  function ensureModal() {
    let modal = document.getElementById(MODAL_ID);
    if (modal) return modal;

    modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.className = 'beurredor-postcode-modal';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="beurredor-postcode-modal__backdrop" data-beurredor-modal-close></div>
      <div class="beurredor-postcode-modal__panel" role="dialog" aria-modal="true" aria-labelledby="beurredor-postcode-title">
        <button type="button" class="beurredor-postcode-modal__close" aria-label="Fermer" data-beurredor-modal-close>×</button>
        <h2 id="beurredor-postcode-title">Votre date de livraison</h2>
        <p>Indiquez votre code postal pour afficher automatiquement la date sur tous les produits.</p>
        <form data-beurredor-postcode-form>
          <label for="beurredor-postcode-input">Votre code postal</label>
          <input id="beurredor-postcode-input" name="postcode" inputmode="numeric" autocomplete="postal-code" maxlength="5" pattern="[0-9]{5}" required>
          <p class="beurredor-postcode-modal__error" data-beurredor-postcode-error hidden>Entrez un code postal français à 5 chiffres.</p>
          <button type="submit" class="beurredor-postcode-modal__submit">Afficher ma livraison</button>
        </form>
      </div>
    `;
    document.body.appendChild(modal);
    return modal;
  }

  function openModal() {
    const modal = ensureModal();
    const input = modal.querySelector('#beurredor-postcode-input');
    const error = modal.querySelector('[data-beurredor-postcode-error]');
    if (input) input.value = getPostcode();
    if (error) error.hidden = true;
    modal.hidden = false;
    document.documentElement.classList.add('beurredor-modal-open');
    window.requestAnimationFrame(() => input?.focus());
  }

  function closeModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('beurredor-modal-open');
  }

  function handleClick(event) {
    const editButton = event.target.closest('[data-beurredor-postcode-edit]');
    if (editButton) {
      event.preventDefault();
      event.stopPropagation();
      openModal();
      return;
    }

    if (event.target.closest('[data-beurredor-modal-close]')) {
      event.preventDefault();
      closeModal();
    }
  }

  function handleSubmit(event) {
    const form = event.target.closest('[data-beurredor-postcode-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('#beurredor-postcode-input');
    const error = form.querySelector('[data-beurredor-postcode-error]');
    const postcode = normalizePostcode(input?.value);
    if (!postcode) {
      if (error) error.hidden = false;
      input?.focus();
      return;
    }

    savePostcode(postcode);
    updateCardEstimates(postcode);
    updateProductEstimate(postcode);
    closeModal();
  }

  function scheduleInstall() {
    if (observerScheduled) return;
    observerScheduled = true;
    window.requestAnimationFrame(() => {
      observerScheduled = false;
      installAll();
    });
  }

  function start() {
    ensureModal();
    installAll();
    document.addEventListener('click', handleClick);
    document.addEventListener('submit', handleSubmit);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal();
    });
    document.addEventListener('shopify:section:load', scheduleInstall);

    const observer = new MutationObserver(scheduleInstall);
    observer.observe(document.body, { childList: true, subtree: true });
    window.setInterval(installAll, 5 * 60 * 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
